/**
 * Authenticated route group layout.
 *
 * Guards this entire segment: if the session is not authenticated, redirect to
 * /login. (The root AuthGate in app/_layout.tsx handles this too, but the
 * in-group guard is a defence-in-depth measure for deep-links.)
 *
 * Renders a native <Stack> (expo-router) once authenticated. The stack ROOT is
 * the `(tabs)` group — a persistent bottom tab bar over the five top-level
 * sections (Home / Queue / Calendar / Estimates / Account). The tabs group
 * manages its own headers, so its Stack header here is hidden.
 *
 * Invoices + Review are NOT tabs (≤5 tab budget) — they live on this parent
 * stack and are reached as tiles on the Home hub. Every OTHER route registered
 * below (estimates/new, estimates/[id], invoices, invoices/[id], review,
 * schedule/[id], tasks/[id], provenance/[factId], capture, voice, assistant,
 * estimator, import, onboarding) is a detail / action screen that gets PUSHED on
 * this stack OVER the tabs: full-screen, with a header + back button + Android
 * hardware-back, and the tab bar hidden. Popping returns to the originating tab.
 */

import { useEffect } from 'react';
import { Stack, useRouter, useRootNavigationState } from 'expo-router';
import { useSession } from '../../src/auth/session';
import { useLiveSync } from '../../src/realtime/liveSync';
import { useWebShortcuts } from '../../src/hooks/useWebShortcuts';
import { useConnectivity } from '../../src/offline/useConnectivity';
import { theme } from '../../src/theme';
import { HeaderIcons } from '../../src/components/nav/HeaderIcons';

// Brand-consistent native-stack header. Flat (no shadow) to match the
// border-only list/card style; paper background; ink title in the brand
// display face. Used by both this stack and the nested account stack.
export const stackScreenOptions = {
  headerStyle: { backgroundColor: theme.color.bg.app },
  headerTintColor: theme.color.action.primary, // back chevron + interactive tint
  // Brand display face for the header title, sized from the `subhead` token
  // (no raw values — keeps style-guard happy).
  headerTitleStyle: {
    color: theme.color.text.primary,
    fontFamily: theme.type.subhead.fontFamily,
    fontSize: theme.type.subhead.fontSize,
  },
  headerShadowVisible: false, // flat brand — no drop shadow / bottom hairline
  headerBackTitleVisible: false,
  contentStyle: { backgroundColor: theme.color.bg.app },
} as const;

export default function AppLayout() {
  const { status } = useSession();
  const router = useRouter();
  const navState = useRootNavigationState();

  // W3 — live-sync (web): subscribe to SSE invalidation hints while authenticated. No-op on native.
  useLiveSync();
  // W5 — desktop keyboard navigation shortcuts. No-op on native.
  useWebShortcuts();
  // REM-MOB-01 — mount the offline auto-drain at the APP SHELL so a capture queued offline flushes on
  // reconnect regardless of the current screen (previously only home.tsx/review.tsx mounted it, so a
  // reconnect on any other screen left the queue sitting until the user navigated back).
  useConnectivity();

  useEffect(() => {
    if (!navState?.key) return; // wait until the root navigator is mounted
    if (status === 'unauthenticated') {
      router.replace('/login');
    }
  }, [status, router, navState?.key]);

  if (status !== 'authenticated') return null; // safe: the root <Slot/> is already mounted

  return (
    <Stack
      screenOptions={({ route }) => ({
        ...stackScreenOptions,
        headerRight: () => <HeaderIcons routeName={route.name} />,
      })}
    >
      {/* Stack ROOT — the persistent bottom tab bar. The tabs group manages its
          own headers, so hide this one. */}
      <Stack.Screen name="(tabs)" options={{ headerShown: false }} />

      {/* Detail / action routes — pushed OVER the tabs (full-screen, tab bar
          hidden, header + back). Resolved paths are unchanged by the (tabs)
          group nesting, so existing router.push targets keep working. */}
      <Stack.Screen name="estimates/new" options={{ title: 'New Estimate' }} />
      <Stack.Screen name="estimates/[id]" options={{ title: 'Estimate' }} />
      <Stack.Screen name="estimates/add-service" options={{ title: 'Add a Service' }} />
      <Stack.Screen name="estimates/add-material" options={{ title: 'Add a Material' }} />
      <Stack.Screen name="invoices" options={{ title: 'Invoices' }} />
      <Stack.Screen name="invoices/[id]" options={{ title: 'Invoice' }} />
      <Stack.Screen name="review" options={{ title: 'Review Queue' }} />
      <Stack.Screen name="provenance/[factId]" options={{ title: 'Provenance' }} />
      <Stack.Screen name="schedule/[id]" options={{ title: 'Event' }} />
      <Stack.Screen name="tasks/[id]" options={{ title: 'Task' }} />

      <Stack.Screen name="assistant" options={{ title: 'Assistant' }} />
      <Stack.Screen name="estimator" options={{ title: 'Estimate Draft' }} />

      <Stack.Screen name="capture" options={{ title: 'Capture Receipt' }} />
      <Stack.Screen name="voice" options={{ title: 'Voice Note' }} />
      <Stack.Screen name="import" options={{ title: 'Import History' }} />
      <Stack.Screen name="onboarding" options={{ title: 'Get Started' }} />

      {/* "More" hub + the full-feature modules reached from it. */}
      <Stack.Screen name="more" options={{ title: 'More' }} />
      <Stack.Screen name="customers" options={{ title: 'Customers' }} />
      <Stack.Screen name="customers/[id]" options={{ title: 'Customer' }} />
      <Stack.Screen name="tasks/new" options={{ title: 'New Task' }} />

      <Stack.Screen name="punchlists" options={{ title: 'Punchlists' }} />
      <Stack.Screen name="punchlists/[id]" options={{ title: 'Punchlist' }} />
      <Stack.Screen name="subcontractors" options={{ title: 'Subcontractors' }} />
      <Stack.Screen name="subcontractors/[id]" options={{ title: 'Subcontractor' }} />

      <Stack.Screen name="supplier-pricing" options={{ title: 'Supplier Pricing' }} />
      <Stack.Screen name="supplier-pricing/connect" options={{ title: 'Connect a Price Source' }} />
      <Stack.Screen name="supplier-pricing/[id]" options={{ title: 'Connection' }} />
      <Stack.Screen name="supplier-pricing/connector/[id]" options={{ title: 'Connector' }} />
      <Stack.Screen name="supplier-pricing/import/[id]" options={{ title: 'Price Import' }} />

      <Stack.Screen name="messages" options={{ title: 'Messages' }} />
      <Stack.Screen name="messages/compose" options={{ title: 'Compose' }} />
      <Stack.Screen name="messages/templates" options={{ title: 'Templates' }} />
      <Stack.Screen name="messages/automations" options={{ title: 'Automations' }} />

      <Stack.Screen name="permits" options={{ title: 'Permits & NOC' }} />
      <Stack.Screen name="permits/new" options={{ title: 'New Filing' }} />
      <Stack.Screen name="permits/prepare" options={{ title: 'Prepare Application' }} />
      <Stack.Screen name="permits/noc" options={{ title: 'Notice of Commencement' }} />
      <Stack.Screen name="permits/manage" options={{ title: 'Manage Permitting' }} />
      <Stack.Screen name="permits/packet" options={{ title: 'Submission Packet' }} />
      <Stack.Screen name="permits/file" options={{ title: 'File Permit' }} />
      <Stack.Screen name="permits/lifecycle" options={{ title: 'Lifecycle & Corrections' }} />
      <Stack.Screen name="permits/by-job" options={{ title: 'Job Permits' }} />
      <Stack.Screen name="permits/[id]" options={{ title: 'Permit Application' }} />

      <Stack.Screen name="government" options={{ title: 'Government' }} />
      <Stack.Screen name="government/add" options={{ title: 'Add / Configure' }} />
      <Stack.Screen name="government/[id]" options={{ title: 'Solicitation' }} />
      <Stack.Screen name="government/registration/[id]" options={{ title: 'Federal Registration' }} />
      <Stack.Screen name="government/proposal/[id]" options={{ title: 'Proposal' }} />

      <Stack.Screen name="reports" options={{ title: 'Reports' }} />
      <Stack.Screen name="jobs/index" options={{ title: 'Jobs' }} />
      <Stack.Screen name="jobs/[id]" options={{ title: 'Job' }} />

      <Stack.Screen name="help" options={{ title: 'Help' }} />
    </Stack>
  );
}
